// src/pages/CashGames.js
import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { db } from "../firebase";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";

const COPPER = "#B87333";

function money(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function seatsToGrid(seats) {
  const arr = Array.isArray(seats) ? seats : [];
  const map = new Map(arr.map((s) => [Number(s.seat), s]));
  return Array.from({ length: 9 }, (_, i) => map.get(i + 1) || { seat: i + 1, empty: true });
}

// ---------- Chip helpers ----------
function normalizeChipEntries(table) {
  const chips = Array.isArray(table?.chipSetChips) ? table.chipSetChips : [];

  const normalized = chips
    .map((c) => {
      const value =
        Number(c?.value) ||
        Number(c?.denom) ||
        Number(c?.amount) ||
        Number(c?.chipValue) ||
        0;
      const color = String(c?.color || c?.hex || c?.fill || "").trim();
      return { value, color };
    })
    .filter((c) => Number.isFinite(c.value) && c.value > 0)
    .sort((a, b) => a.value - b.value);

  if (!normalized.length) {
    const denoms = Array.isArray(table?.chipSetDenoms) ? table.chipSetDenoms : [];
    const cleaned = denoms
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x) && x > 0)
      .sort((a, b) => a - b);
    return cleaned.map((v) => ({ value: v, color: "" }));
  }

  return normalized;
}

function formatDenomLabel(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1) return money(n).replace(".00", "");
  return money(n);
}

function ChipsIcon({ size = 14 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path
        d="M12 22c4.97 0 9-1.79 9-4V6c0-2.21-4.03-4-9-4S3 3.79 3 6v12c0 2.21 4.03 4 9 4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M21 10c0 2.21-4.03 4-9 4s-9-1.79-9-4" stroke="currentColor" strokeWidth="2" />
      <path d="M21 14c0 2.21-4.03 4-9 4s-9-1.79-9-4" stroke="currentColor" strokeWidth="2" />
      <path d="M12 2v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8.5 3.1l1.6 5.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15.5 3.1l-1.6 5.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Chevron({ open }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={open ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Denomination list with tiny chip-colored dots.
 */
function DenomsInlineChipDots({ items }) {
  const arr = Array.isArray(items) ? items : [];
  if (!arr.length) return <span>—</span>;

  const fallback = [
    "#22c55e",
    "#3b82f6",
    "#a855f7",
    "#f59e0b",
    "#ef4444",
    "#06b6d4",
    "#f97316",
    "#e11d48",
    "#84cc16",
  ];

  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
      {arr.map((chip, idx) => {
        const label = formatDenomLabel(chip.value);
        if (!label) return null;

        const bg = chip.color || fallback[idx % fallback.length];

        return (
          <span key={`${chip.value}-${idx}`} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block rounded-full"
              style={{
                width: 7,
                height: 7,
                backgroundColor: bg,
                boxShadow: "0 0 0 1px rgba(0,0,0,0.55), 0 0 0 2px rgba(255,255,255,0.06)",
                flex: "0 0 auto",
              }}
              aria-hidden="true"
              title={chip.color ? `Chip color: ${chip.color}` : "Chip color"}
            />
            <span className="whitespace-nowrap">{label}</span>
          </span>
        );
      })}
    </span>
  );
}

// ---------- Avatar helpers ----------
function initialsFromName(name) {
  const s = String(name || "").trim();
  if (!s) return "";
  const parts = s.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
  return (first + last).toUpperCase();
}

/**
 * Big right-side avatar that fills the entire right column of the seat card.
 */
function SeatAvatarBig({ seat }) {
  const name = seat?.name || seat?.displayName || seat?.email || "";
  const photoUrl = seat?.photoUrl || seat?.profilePhotoUrl || "";

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name ? `${name} avatar` : "avatar"}
        className="h-full w-full object-cover"
        style={{
          borderLeft: "1px solid rgba(184,115,51,0.14)",
        }}
        loading="lazy"
      />
    );
  }

  const initials = initialsFromName(name);

  return (
    <div
      className="h-full w-full flex items-center justify-center font-extrabold"
      style={{
        borderLeft: "1px solid rgba(184,115,51,0.14)",
        background: "rgba(184,115,51,0.10)",
        color: COPPER,
        fontSize: 18,
        letterSpacing: 1,
      }}
      title={name || "No player"}
      aria-hidden="true"
    >
      {initials || "—"}
    </div>
  );
}

function TableCard({ table, chipsOpen, setChipsOpen }) {
  const seats = useMemo(() => seatsToGrid(table.seats), [table.seats]);

  const title = table.displayName || `${table.stakesLabel || "Table"} — #${table.tableNumber ?? ""}`;

  const chipName = table.chipSetName || table.chipSet || null;
  const chipEntries = useMemo(() => normalizeChipEntries(table), [table]);

  const hasChipInfo = !!chipName;

  return (
    <div
      className="rounded-xl border bg-zinc-950 p-4"
      style={{
        borderColor: "rgba(184,115,51,0.22)",
        boxShadow: table.bombPotsEnabled ? "0 0 18px rgba(184,115,51,0.12)" : "none",
      }}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {/* Title + bomb indicator */}
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="text-xl font-bold truncate" style={{ color: COPPER }}>
              {title}
            </h3>

            {table.bombPotsEnabled && (
              <span
                className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                style={{
                  background: "rgba(184,115,51,0.15)",
                  border: "1px solid rgba(184,115,51,0.35)",
                  color: COPPER,
                }}
                title="Bomb pots are enabled at this table"
              >
                💣 Bomb Pots On
              </span>
            )}
          </div>

          <p className="text-zinc-400 mt-1 text-sm">
            Stakes: <span className="text-zinc-200 font-semibold">{table.stakesLabel || "—"}</span>
            {" • "}
            Buy-in:{" "}
            <span className="text-zinc-200 font-semibold">
              {money(table.minBuyIn)}–{money(table.maxBuyIn)}
            </span>
          </p>

          {/* ===== Chip set + denominations ===== */}
          {/* Desktop: always show */}
          <div className="hidden sm:block mt-1 text-sm text-zinc-400">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center h-6 w-6 rounded-md border"
                style={{
                  borderColor: "rgba(184,115,51,0.25)",
                  color: COPPER,
                  background: "rgba(184,115,51,0.06)",
                }}
                title="Chip set"
              >
                <ChipsIcon size={14} />
              </span>

              <div className="min-w-0">
                Chip Set:{" "}
                <span className="text-zinc-200 font-semibold">{chipName ? chipName : "Not set"}</span>

                {/* ✅ NO bullet here */}
                {chipName ? (
                  <span className="text-zinc-500 ml-2">
                    <DenomsInlineChipDots items={chipEntries} />
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {/* Mobile: tap to expand/collapse */}
          <div className="sm:hidden mt-2">
            <button
              type="button"
              onClick={() => setChipsOpen((v) => !v)}
              className="w-full flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left"
              style={{
                borderColor: "rgba(184,115,51,0.18)",
                background: "rgba(255,255,255,0.02)",
              }}
              aria-expanded={chipsOpen ? "true" : "false"}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="inline-flex items-center justify-center h-7 w-7 rounded-md border"
                  style={{
                    borderColor: "rgba(184,115,51,0.25)",
                    color: COPPER,
                    background: "rgba(184,115,51,0.06)",
                  }}
                >
                  <ChipsIcon size={14} />
                </span>

                <div className="min-w-0">
                  <div className="text-sm font-semibold text-zinc-200 truncate">
                    Chip Set: {chipName ? chipName : "Not set"}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">Tap to view denominations</div>
                </div>
              </div>

              <span className="text-zinc-300">
                <Chevron open={chipsOpen} />
              </span>
            </button>

            {chipsOpen && (
              <div
                className="mt-2 rounded-lg border px-3 py-2 text-sm"
                style={{
                  borderColor: "rgba(184,115,51,0.18)",
                  background: "rgba(0,0,0,0.18)",
                }}
              >
                <div className="text-zinc-400">
                  Chip Set:{" "}
                  <span className="text-zinc-200 font-semibold">{chipName ? chipName : "Not set"}</span>
                </div>

                <div className="text-zinc-500 mt-1">
                  Denominations:{" "}
                  <span className="text-zinc-200 font-semibold">
                    {hasChipInfo ? <DenomsInlineChipDots items={chipEntries} /> : "—"}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Seats grid */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        {seats.map((s) => {
          const occupied = !(s.empty || (!s.name && !s.uid && !s.email));
          const displayName = s.name || s.displayName || s.email || "—";

          return (
            <div
              key={s.seat}
              className="rounded-lg border overflow-hidden"
              style={{
                borderColor: "rgba(184,115,51,0.14)",
                background: "rgba(255,255,255,0.02)",
              }}
            >
              {/* New layout: left content + full-height right avatar */}
              <div className="flex items-stretch min-h-[72px]">
                {/* Left */}
                <div className="flex-1 px-3 py-2 min-w-0">
                  <div className="text-xs text-zinc-500">Seat {s.seat}</div>
                  <div className="text-zinc-100 font-semibold truncate mt-1">
                    {occupied ? displayName : "—"}
                  </div>
                </div>

                {/* Right avatar column */}
                <div
                  className="w-[72px] shrink-0"
                  style={{
                    background: occupied ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.10)",
                  }}
                >
                  {occupied ? <SeatAvatarBig seat={s} /> : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CashGames() {
  const [tables, setTables] = useState([]);
  const [chipOpenById, setChipOpenById] = useState({}); // { [tableId]: boolean }

  useEffect(() => {
    const ref = collection(db, "cashTables");
    const qRef = query(ref, orderBy("stakesKey", "asc"), orderBy("tableNumber", "asc"));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setTables(list);

        // cleanup open states for removed tables
        setChipOpenById((prev) => {
          const next = { ...prev };
          const ids = new Set(list.map((t) => t.id));
          Object.keys(next).forEach((k) => {
            if (!ids.has(k)) delete next[k];
          });
          return next;
        });
      },
      (err) => console.error("Cash tables snapshot error:", err)
    );

    return () => unsub();
  }, []);

  return (
    <Layout>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: COPPER }}>
            Cash Games
          </h2>
          <p className="text-zinc-400 mt-2">View-only. Seats and table settings are managed by admins.</p>
        </div>
      </div>

      {tables.length === 0 ? (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 text-zinc-300">No cash game tables yet.</div>
      ) : (
        <div className="grid gap-4">
          {tables.map((t) => (
            <TableCard
              key={t.id}
              table={t}
              chipsOpen={!!chipOpenById[t.id]}
              setChipsOpen={(fn) =>
                setChipOpenById((prev) => ({
                  ...prev,
                  [t.id]: typeof fn === "function" ? fn(!!prev[t.id]) : !!fn,
                }))
              }
            />
          ))}
        </div>
      )}
    </Layout>
  );
}